import React, { useState, useEffect } from 'react';
import { Search, Tag, ChevronRight, ChevronDown, RefreshCw, LogIn, Trash2 } from 'lucide-react';

// Composant principal
export default function DockerRegistryViewer() {
  const [registryUrl, setRegistryUrl] = useState(process.env.REACT_APP_REGISTRY_URL || 'http://registry:5000');
  const [username, setUsername] = useState(process.env.REACT_APP_REGISTRY_USERNAME || '');
  const [password, setPassword] = useState(process.env.REACT_APP_REGISTRY_PASSWORD || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRepo, setExpandedRepo] = useState(null);
  const [tags, setTags] = useState({});
  const [filter, setFilter] = useState('');
  const [deletingTag, setDeletingTag] = useState(null);

  // Créer les en-têtes pour les requêtes
  // Les requêtes passent par le proxy nginx (/api/registry/)
  // Le header X-Registry-URL indique au proxy vers quel registry relayer
  const getHeaders = () => {
    const headers = {
      'X-Registry-URL': registryUrl.replace(/\/+$/, '')
    };
    if (username && password) {
      headers['Authorization'] = `Basic ${btoa(`${username}:${password}`)}`;
    }
    return headers;
  };

  // Fonction pour récupérer les dépôts
  const fetchRepositories = async () => {
    if (!authenticated && (username || password)) {
      // Tester l'authentification d'abord
      try {
        const response = await fetch('/api/registry/v2/', {
          headers: getHeaders()
        });
        
        if (response.status === 401) {
          setError("Erreur d'authentification. Vérifiez vos identifiants.");
          setAuthenticated(false);
          return;
        }
        
        setAuthenticated(true);
      } catch (err) {
        setError(`Erreur de connexion au registry: ${err.message}`);
        return;
      }
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/registry/v2/_catalog', {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          setError("Authentification requise. Veuillez saisir vos identifiants.");
          setAuthenticated(false);
        } else {
          throw new Error(`Erreur HTTP ${response.status}`);
        }
        return;
      }
      
      const data = await response.json();
      setRepositories(data.repositories || []);
      setAuthenticated(true);
    } catch (err) {
      setError(`Erreur lors de la récupération des dépôts: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour récupérer les tags d'un dépôt
  const fetchTags = async (repository) => {
    try {
      const response = await fetch(`/api/registry/v2/${repository}/tags/list`, {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setTags(prev => ({
        ...prev,
        [repository]: data.tags || []
      }));
    } catch (err) {
      console.error(`Erreur lors de la récupération des tags pour ${repository}:`, err);
      setTags(prev => ({
        ...prev,
        [repository]: ['Erreur de chargement']
      }));
    }
  };

  // Fonction pour supprimer une image (un tag) du registry
  // L'API Docker supprime par digest et non par tag :
  //   1. On récupère le digest du manifest via le header Docker-Content-Digest
  //   2. On envoie un DELETE sur ce digest
  const deleteTag = async (repository, tag) => {
    if (!window.confirm(
      `Supprimer définitivement « ${repository}:${tag} » ?\n\n` +
      `Attention : si plusieurs tags pointent vers la même image, ils seront tous supprimés. ` +
      `L'espace disque n'est libéré qu'après le garbage collection du registry.`
    )) {
      return;
    }

    setDeletingTag(`${repository}:${tag}`);
    setError(null);

    try {
      // Étape 1 : récupérer le digest du manifest
      const manifestResponse = await fetch(`/api/registry/v2/${repository}/manifests/${tag}`, {
        method: 'GET',
        headers: {
          ...getHeaders(),
          'Accept': [
            'application/vnd.docker.distribution.manifest.v2+json',
            'application/vnd.docker.distribution.manifest.list.v2+json',
            'application/vnd.oci.image.manifest.v1+json',
            'application/vnd.oci.image.index.v1+json'
          ].join(', ')
        }
      });

      if (!manifestResponse.ok) {
        throw new Error(`Impossible de récupérer le manifest (HTTP ${manifestResponse.status})`);
      }

      const digest = manifestResponse.headers.get('Docker-Content-Digest');
      if (!digest) {
        throw new Error("Le registry n'a pas renvoyé de digest (header Docker-Content-Digest manquant).");
      }

      // Étape 2 : supprimer le manifest par son digest
      const deleteResponse = await fetch(`/api/registry/v2/${repository}/manifests/${digest}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (deleteResponse.status === 405) {
        throw new Error(
          "La suppression est désactivée sur le registry. Ajoutez REGISTRY_STORAGE_DELETE_ENABLED=true."
        );
      }
      if (deleteResponse.status !== 202 && !deleteResponse.ok) {
        throw new Error(`Échec de la suppression (HTTP ${deleteResponse.status})`);
      }

      // Mise à jour locale : on retire le tag supprimé de la liste
      setTags(prev => ({
        ...prev,
        [repository]: (prev[repository] || []).filter(t => t !== tag)
      }));
    } catch (err) {
      setError(`Erreur lors de la suppression de ${repository}:${tag} : ${err.message}`);
      console.error(err);
    } finally {
      setDeletingTag(null);
    }
  };

  // Exécuter la connexion lorsque les identifiants changent
  useEffect(() => {
    if (!authenticated && (username || password)) {
      fetchRepositories();
    }
  }, [username, password]);

  // Filtrer les dépôts en fonction de la recherche
  const filteredRepositories = repositories.filter(repo => 
    repo.toLowerCase().includes(filter.toLowerCase())
  );

  // Gérer l'expansion d'un dépôt
  const toggleRepository = (repository) => {
    if (expandedRepo === repository) {
      setExpandedRepo(null);
    } else {
      setExpandedRepo(repository);
      if (!tags[repository]) {
        fetchTags(repository);
      }
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* En-tête */}
      <header className="bg-blue-600 text-white p-4 shadow-md">
        <div className="container mx-auto">
          <h1 className="text-2xl font-bold">Visualiseur de Registry Docker</h1>
          <div className="flex items-center mt-4 space-x-2">
            <input
              type="text"
              value={registryUrl}
              onChange={(e) => setRegistryUrl(e.target.value)}
              className="flex-grow p-2 rounded text-gray-800"
              placeholder="URL du registry (ex: http://registry:5000)"
            />
            <button
              onClick={fetchRepositories}
              className="bg-blue-700 p-2 rounded hover:bg-blue-800 flex items-center"
            >
              <RefreshCw size={18} />
              <span className="ml-1">Rafraîchir</span>
            </button>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="flex-grow container mx-auto p-4">
        {/* Section d'authentification */}
        {!authenticated && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-semibold mb-4">Authentification au Registry</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 mb-2">Nom d'utilisateur</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Mot de passe</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <button
                onClick={fetchRepositories}
                className="bg-blue-600 text-white p-2 rounded hover:bg-blue-700 flex items-center justify-center w-full"
              >
                <LogIn size={18} className="mr-2" />
                Se connecter
              </button>
            </div>
          </div>
        )}

        {/* Barre de recherche (visible seulement si authentifié) */}
        {authenticated && (
          <div className="mb-4 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Filtrer les dépôts..."
              className="pl-10 p-2 w-full rounded border border-gray-300"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        )}

        {/* Affichage des erreurs */}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
            <p>{error}</p>
          </div>
        )}

        {/* Indicateur de chargement */}
        {loading ? (
          <div className="text-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des dépôts...</p>
          </div>
        ) : (
          authenticated && (
            <>
              {/* Liste des dépôts */}
              {filteredRepositories.length > 0 ? (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <ul className="divide-y divide-gray-200">
                    {filteredRepositories.map((repository) => (
                      <li key={repository} className="hover:bg-gray-50">
                        <div 
                          className="px-4 py-3 cursor-pointer"
                          onClick={() => toggleRepository(repository)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              {expandedRepo === repository ? (
                                <ChevronDown size={20} className="text-gray-500 mr-2" />
                              ) : (
                                <ChevronRight size={20} className="text-gray-500 mr-2" />
                              )}
                              <span className="font-medium text-gray-800">{repository}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Tags du dépôt */}
                        {expandedRepo === repository && (
                          <div className="bg-gray-50 px-4 py-3 border-t border-gray-100">
                            {!tags[repository] ? (
                              <div className="text-center p-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-700 mx-auto"></div>
                                <p className="mt-2 text-sm text-gray-600">Chargement des tags...</p>
                              </div>
                            ) : tags[repository].length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {tags[repository].map((tag) => (
                                  <div
                                    key={tag}
                                    className="flex items-center justify-between bg-white p-2 rounded border border-gray-200 hover:border-blue-300"
                                  >
                                    <div className="flex items-center min-w-0">
                                      <Tag size={16} className="text-blue-500 mr-2 flex-shrink-0" />
                                      <span className="text-gray-700 truncate">{tag}</span>
                                    </div>
                                    <button
                                      onClick={() => deleteTag(repository, tag)}
                                      disabled={deletingTag === `${repository}:${tag}`}
                                      title="Supprimer cette image"
                                      className="ml-2 p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 flex-shrink-0"
                                    >
                                      {deletingTag === `${repository}:${tag}` ? (
                                        <RefreshCw size={16} className="animate-spin" />
                                      ) : (
                                        <Trash2 size={16} />
                                      )}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 italic">Aucun tag trouvé pour ce dépôt</p>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-center p-12 bg-white rounded-lg shadow">
                  <p className="text-gray-500">
                    {filter ? 'Aucun dépôt ne correspond à votre recherche.' : 'Aucun dépôt trouvé dans ce registry.'}
                  </p>
                </div>
              )}
            </>
          )
        )}
      </main>

      {/* Pied de page */}
      <footer className="bg-gray-100 border-t border-gray-200 p-4">
        <div className="container mx-auto text-center text-gray-600 text-sm">
          <p>Interface Registry Docker - Développée pour simplifier la visualisation de vos images</p>
          <p className="mt-1">Accédez à votre registry à {registryUrl}</p>
        </div>
      </footer>
    </div>
  );
}
